import throttle from 'lodash-es/throttle';
import isEqual from 'lodash-es/isEqual';
import { createEmpty, extend, getHeight, getWidth, isEmpty } from 'ol/extent';
import { unByKey } from 'ol/Observable';
import Projection from 'ol/proj/Projection';
import View from 'ol/View';
import { PROJECTIONS as WEB_MERCATOR_PROJECTIONS } from 'ol/proj/epsg3857';
import { Layer } from 'ol/layer';

import { getActionContext } from 'cadenza/action-context';
import { getSelectedLayerFeatures } from 'cadenza/api-client/maps-api';
import { setupViewActivation } from 'cadenza/api-client/views-api';
import { HighlightHandler } from 'cadenza/map/highlight-layer/highlight-handler';
import { createLayer, refreshLayer, scaleToResolution, updateLayer, WORKBOOK_VIEW_ON_DEMAND } from 'cadenza/map/layer-utils';
import { MapProgressSpinner } from 'cadenza/map/map-progress-spinner';
import { calculateMapViewPadding, getControl, resolutionForScale, scaleForResolution } from 'cadenza/map/map-utils';
import { MapView } from 'cadenza/map/map-view';
import { logger } from 'cadenza/utils/logging';
import { roundToFloatOrInt } from 'cadenza/utils/math';
import type { GroupChoiceDto, NormalGroupChoices } from 'cadenza/api-client/classic-map-context-api';
import { SourceProperty } from 'cadenza/map/source-property';
import { Observable, on } from 'cadenza/utils/event-util';
import { WorkbookHighlightHandler } from 'cadenza/map/custom/workbook/workbook-highlight-handler';
import { cadenzaSessionStorage } from 'cadenza/utils/storage/storage';
import { context } from 'cadenza/map/context/context';
import { LayerProperty } from 'cadenza/map/layer-property';
import { MapProperty } from 'cadenza/map/map-property';
import { ViewProperty } from 'cadenza/map/view-property';
import type { Map } from 'cadenza/map/map';
import type { ProjectionDto } from 'cadenza/api-client/dto/projection-dto';
import type { ClassicLayerDto } from 'cadenza/api-client/dto/classic-layer-dto';
import type { LayerAction } from 'cadenza/map/layer-actions/layer-action';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';
import { getAllLayers } from 'cadenza/map/map-layer-utils';
import { ClassicMapContextApi } from 'cadenza/api-client/classic-map-context-api';
import type { LayerDto } from 'cadenza/api-client/dto/layer-dto';

import type { ViewOptions } from 'ol/View';
import type { Extent } from 'ol/extent';
import type Feature from 'ol/Feature';
import type { EventsKey } from 'ol/events';
import type BaseLayer from 'ol/layer/Base';

export interface ClassicMapContextInitializationOptions {
  projection: ProjectionDto;
  currentExtent: Extent;
  maxExtent: Extent;
  minAllowedScale: number;
  maxAllowedScale: number;
  constrainZoomScale: boolean;
  forceMinMaxScale: boolean;
}

const DEFAULT_NUMBER_OF_ZOOM_LEVELS = 25;
const ZOOM_SCALE_FACTOR = 2;

/**
 * There is only one selected layer at a time. On the client, we maintain this info as a property of the map, not of the layer.
 * That's why we do not add this to cadenza/map/layer-property."
 */
const LAYER_SELECTED_PROPERTY = 'selected';

function toggleGroupActiveLayerChanged (existingLayer: BaseLayer, layerProps: ClassicLayerDto) {
  const previouslyActive = existingLayer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX);
  const currentlyActive = layerProps.toggleGroupActiveLayerIndex;
  return previouslyActive && (previouslyActive !== currentlyActive);
}

/**
 * Please do *not* instantiate this directly and instead use cadenza/map/context, i.e. context(map)
 */
export class ClassicMapContext extends Observable {

  readonly #mapContextApi;
  readonly #contextPushEnabled;
  /**
   * List of IDs of views or data views that need to be invalidated.
   */
  #changedLayerIds?: string[];
  readonly #map: Map;
  readonly #mapProgressSpinner: MapProgressSpinner;
  readonly #highlightHandler: WorkbookHighlightHandler | HighlightHandler;
  readonly #onVisibilityChange: () => void;
  #eventKeys: EventsKey[] = [];
  #zoomLevelBeforeMove: number | undefined;
  #isPullInProgress = false;
  readonly #pushIfNotPullingThrottled = throttle(this._pushIfNotPulling.bind(this), 500);

  constructor (map: Map) {
    super();
    this.#map = map;
    const mapId = map.get(MapProperty.ID);
    this.#mapContextApi = new ClassicMapContextApi({ mapId });
    this.#mapProgressSpinner = getControl(map, MapProgressSpinner) as MapProgressSpinner;
    this.#highlightHandler = map.get(MapProperty.IS_WORKBOOK_MAP)
      ? new WorkbookHighlightHandler(map)
      : new HighlightHandler(map);
    this.#contextPushEnabled = map.get(MapProperty.CONTEXT_PUSH_ENABLED);

    this.#onVisibilityChange = () => {
      if (!document.hidden) {
        this._updateCurrentMap();
      }
    };
  }

  /**
   * Initial setup of map context
   *
   * @param mapExtent - Optional override of map extent obtained from server
   * @return Promise that resolves when the pull is finished
   */
  setup (mapExtent?: Extent) {
    const setupPromise = this.pull(mapExtent);

    const map = this.#map;
    this.#eventKeys = [
      map.on('movestart', () => {
        this.#zoomLevelBeforeMove = map.getView().getZoom();
      }),
      map.on('moveend', () => {
        const pushPromise = this.push();

        // If the map is zoomed, the selection count of a group theme can change if a feature layer
        // in the group theme becomes visible/invisible at the new scale.
        if (map.getView().getZoom() !== this.#zoomLevelBeforeMove) {
          this._onZoomChange(pushPromise);
        }
      }),
      map.on('propertychange', (event) => {
        if (event.key === MapProperty.SELECTED_LAYER || event.key === MapProperty.SIZE) {
          this.push();
        }
        if (event.key === MapProperty.PRIMARY_INTERACTION) {
          if (event.oldValue) {
            event.oldValue.setActive(false);
            map.removeInteraction(event.oldValue);
          }
          const newInteraction = map.get(MapProperty.PRIMARY_INTERACTION);
          if (newInteraction) {
            map.addInteraction(newInteraction);
            newInteraction.setActive(true);
          }
        }
      }),
      map.getLayers().on('propertychange', () => {
        const selectedLayer = map.get(MapProperty.SELECTED_LAYER);
        if (!this.layers.includes(selectedLayer)) {
          map.unset(MapProperty.SELECTED_LAYER);
        }
        this.push();
      }),
      map.getLayers().on('remove', ({ element: layer }) => context(layer).teardown())
    ];
    if (this.#contextPushEnabled) {
      this._updateCurrentMap();
      document.addEventListener('visibilitychange', this.#onVisibilityChange);
      setupViewActivation(map.get(MapProperty.ID), true);
    }

    return setupPromise;
  }

  _onZoomChange (pushPromise: Promise<void>) {
    const selectedLayer = this.#map.get(MapProperty.SELECTED_LAYER);
    if (selectedLayer && selectedLayer.get(LayerProperty.IS_GROUP_THEME) && selectedLayer.get(LayerProperty.HAS_FEATURES)) {
      pushPromise
        .then(() => {
          const srsCode = this.#map.getView().getProjection().getCode();
          return getSelectedLayerFeatures(layerId(selectedLayer), srsCode, this.mapScale);
        })
        .then(({ features }) => {
          /*
           * When Cadenza is embedded in a businessApp we need to forward the new selection to the businessAp
           *
           * The issue is: OL BaseObject.prototype.set sets the new value and sends notifications whenever oldValue !== newValue,
           * Since [] !== [] is always true, we send notifications on every zoom even if nothing is selected!
           */
          const newSelectedFeatureIDs = features.map(({ id }) => id);
          const hasSelectionChanged = !isEqual(selectedLayer.get(LayerProperty.SELECTED_FEATURE_IDS), newSelectedFeatureIDs);
          if (hasSelectionChanged) {
            selectedLayer.set(LayerProperty.SELECTED_FEATURE_IDS, newSelectedFeatureIDs);
          }
        })
        .catch((error) => logger.error('Failed to refresh selection count', error));
    }
  }

  _updateCurrentMap () {
    cadenzaSessionStorage.setItem('currentMapId', this.#map.get(MapProperty.ID));
  }

  teardown () {
    unByKey(this.#eventKeys);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  /**
   * Loads map metadata from the server and updates the map accordingly.
   *
   * @param mapExtent - Optional override of map extent obtained from server
   * then the map view will be fixed to that scale.
   * @return Promise that resolves when the pull is finished
   */
  pull (mapExtent?: Extent) {
    const map = this.#map;
    // AK: skipping image loading in layer-utils/imageLoadFunction leads to spinner not disappearing
    // (MapProgressSpinner._totalCount() === 1)
    this.#mapProgressSpinner.show();
    const pull = this.#mapContextApi.getContext(map.get(MapProperty.CONTEXT_URL), map.getInnerSize())
      .then(({ title, tags = [], projection, currentExtent, maxExtent, layers, minAllowedScale, maxAllowedScale, constrainZoomScale, forceMinMaxScale, defaultCoordinateVisualizationSrs }) => {
        this.#isPullInProgress = true;
        map.setProperties({ title, tags, defaultCoordinateVisualizationSrs });
        const viewIsInitialized = !!map.getView().getCenter();
        if (!viewIsInitialized) {
          this._initializeView({
            projection,
            currentExtent: mapExtent || currentExtent,
            maxExtent,
            minAllowedScale,
            maxAllowedScale,
            constrainZoomScale,
            forceMinMaxScale
          });
          this._scheduleViewExtentReinitializationIfRequired(mapExtent!, minAllowedScale, maxAllowedScale);
        }

        this._updateLayers(layers);
      });
    pull
      .catch(error => logger.error('Failed to pull map context', error))
      .finally(() => {
        this.#isPullInProgress = false;
        this.#mapProgressSpinner.hide();
      });
    return pull;
  }

  _initializeView ({
    projection: { code, units, title, usesDefaultSrs },
    currentExtent,
    maxExtent,
    minAllowedScale,
    maxAllowedScale,
    constrainZoomScale,
    forceMinMaxScale
  }: ClassicMapContextInitializationOptions) {
    const map = this.#map;
    const projection = new Projection({ code, units });
    const { minResolution, maxResolution } = calculateMinMaxResolutions(projection, maxExtent, map.getInnerSize(), minAllowedScale, maxAllowedScale);
    const padding = calculateMapViewPadding(map);

    const mapOptions: ViewOptions = {
      projection,
      extent: maxExtent,
      maxResolution,
      minResolution,
      padding,
      // By default, the whole view extent is constrained to the given extent, so the user is not allowed to see anything outside of the given extent.
      // Thus, always only some part of the given extent is visible. But our aim here is to display the whole given extent in the view.
      // With constrainOnlyCenter = true, the extent constraint is applied to the view center and not the whole extent.
      constrainOnlyCenter: true,
      smoothResolutionConstraint: false,
      smoothExtentConstraint: false,
      constrainResolution: !!constrainZoomScale
    };

    const minAllowedScaleSet = isMinAllowedScaleSet(minAllowedScale);
    const maxAllowedScaleSet = isMaxAllowedScaleSet(maxAllowedScale);

    if (forceMinMaxScale && minResolution && maxResolution && (minAllowedScaleSet || maxAllowedScaleSet)) {
      // Forces open layers to really use defined resolution steps exactly.
      // This settings wins over minResolution and maxResolution options, if resolutions option is defined OL ignores min/maxResolution options.
      // Without this setting min would be inferred from max and probably not the same value as the configured min.
      // Resolution steps are calculated from the min and max resolution values, starting with max and dividing it with 2 till min is reached, adding also min at the end.
      mapOptions.resolutions = this._calcResolutions(maxResolution, minResolution);
    }

    const view = new MapView(mapOptions);
    view.set(ViewProperty.MAX_EXTENT, maxExtent);
    view.set(ViewProperty.PROJECTION_TITLE, title);
    view.set(ViewProperty.USES_DEFAULT_SRS, usesDefaultSrs);

    // Do view.fit() *before* map.setView(view)
    // so that the view already has a center when set.
    // This avoids initialization problems, e.g. with the OverviewMap.
    //
    // "nearest: true" is required to avoid zooming one step out after page refresh.
    // If exactly same extent is given to the view with exactly same size, the resolution
    // calculated by OL is not exactly the same as it was, because of rounding errors.
    // "nearest" ensures, that the best suitable constrained resolution will be set and the map will not be zoomed out to fit the extent.
    view.fit(currentExtent, { nearest: true, size: map.getInnerSize() });
    map.setView(view);
  }

  _calcResolutions (maxResolution: number, minResolution: number) {
    const resolutions = [];
    resolutions.push(maxResolution);
    let half = roundToFloatOrInt(maxResolution / ZOOM_SCALE_FACTOR, 3);
    while (half > minResolution) {
      resolutions.push(half);
      half = roundToFloatOrInt(half / ZOOM_SCALE_FACTOR, 3);
    }
    resolutions.push(minResolution);
    return resolutions;
  }

  // A workbook map view can be initially hidden on mobile devices, where only one view is shown at a time.
  // Unfortunately all hidden workbook views are still fully initialized and do all their internal work, even if they are hidden.
  // So the map view can be initialized even if it is not displayed. In this case map has a size [0, 0], OL defaults it then to [100, 100].
  // We cannot initialize the view extent without knowing the exact map container size.
  // A workaround here is to wait for the first resize event (it means, the view was shown) and reinitialize the view extent.
  // A perfect solution would be to not load anything in the hidden views until they are shown. Then this method can be cleaned up.
  _scheduleViewExtentReinitializationIfRequired (mapExtent: Extent, minAllowedScale: number, maxAllowedScale: number) {
    const map = this.#map;
    const { width, height }  = map.getTargetElement().getBoundingClientRect();
    if (width !== 0 && height !== 0) {
      return;
    }
    // Map size 0 usually means the map is not visible (for example because the whole workbook map view is not displayed),
    // in this case we schedule map current extent recalculation and reset it as soon as window is resized.
    on(window, 'resize', () => {
      this.#mapProgressSpinner.show();
      // mapExtent is the persisted last extent. In case we have mapExtent, just use it. Otherwise request the extent from the backend (by doing a lightweight version of pull request)
      const getMapExtentPromise = mapExtent
        ? Promise.resolve(mapExtent)
        : this.#mapContextApi.getContext(map.get(MapProperty.CONTEXT_URL), map.getInnerSize())
          .then(({ currentExtent }) => currentExtent);
      getMapExtentPromise
        .then(currentExtent => this._reinitializeViewExtent(currentExtent, minAllowedScale, maxAllowedScale))
        .catch(error => logger.error('Failed to pull map context', error))
        .finally(() => {
          this.#mapProgressSpinner.hide();
        });
    }, { once: true, capture: false });
  }

  _reinitializeViewExtent (currentExtent: Extent, minAllowedScale: number, maxAllowedScale: number) {
    const map = this.#map;
    const view = map.getView();

    const maxExtent = view.get(ViewProperty.MAX_EXTENT);
    const projection = view.getProjection();
    // Also recalculate min and max resolution as they depend on the map size.
    const { minResolution, maxResolution } = calculateMinMaxResolutions(projection, maxExtent, map.getInnerSize(), minAllowedScale, maxAllowedScale);
    view.applyOptions_({ minResolution, maxResolution });
    view.fit(currentExtent, { nearest: true, size: map.getInnerSize() });
  }

  _updateLayers (layers: ClassicLayerDto[] = []) {
    const map = this.#map;
    const newLayers: BaseLayer[] = [];
    let layersChanged = false;
    layers.forEach((layerProps, layerIndex) => {
      let existingLayer = this.getLayer(layerProps.id);

      // if layer type changed, remove it from the map and add it as a new layer
      if (existingLayer && (existingLayer.get(LayerProperty.TYPE) !== layerProps.type
        || toggleGroupActiveLayerChanged(existingLayer, layerProps))) {
        map.removeLayer(existingLayer);
        layersChanged = true;
        existingLayer = undefined;
      }

      if (existingLayer && existingLayer.get(LayerProperty.NORMAL_GROUP_CHOICES) !== layerProps.normalGroupChoices) {
        updateLayer(this.#map, existingLayer, layerProps);
        layersChanged = true;
        refreshLayer(existingLayer);
      } else if (existingLayer && existingLayer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX) === layerProps.toggleGroupActiveLayerIndex) {
        const reload = !this.#changedLayerIds || this.#changedLayerIds.includes(existingLayer.get(LayerProperty.ID));
        updateLayer(this.#map, existingLayer, layerProps, reload);
      } else {
        const isExistingToggleGroup = existingLayer
          && layerProps.toggleGroupActiveLayerIndex
          && existingLayer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX);
        const newLayer = createLayer(layerProps as unknown as LayerDto, map);
        context(newLayer).setup(map);
        if (layerIndex === 0 && isExistingToggleGroup) {
          const indexOfOldGroup = this.layers.indexOf(existingLayer!);
          map.getLayers().insertAt(indexOfOldGroup + 1, newLayer);
        } else if (layerIndex > 0) {
          const prevLayerId = layers[layerIndex - 1].id;
          const prevLayer = this.getLayer(prevLayerId)!;
          const prevLayerIndex = this.layers.indexOf(prevLayer);
          map.getLayers().insertAt(prevLayerIndex + 1, newLayer);
        } else {
          map.getLayers().push(newLayer);
        }

        newLayers.push(newLayer);
        layersChanged = true;
      }
    });
    this.layers.forEach((layer) => {
      const id = layerId(layer);
      const activeLayerIndex = layer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX);
      if (!layers.find((layerProps) => layerProps.id === id && layerProps.toggleGroupActiveLayerIndex === activeLayerIndex)) {
        map.removeLayer(layer);
        layersChanged = true;
      }
    });

    // reorder layers
    const mapLayersInNewOrder = layers.map(({ id }) => this.getLayer(id)!);
    const layerOrderChanged = mapLayersInNewOrder.some((layer, index) => index !== this.layers.indexOf(layer));
    if (layerOrderChanged) {
      this._setLayers(mapLayersInNewOrder);
      layersChanged = true;
    }

    if (layersChanged) {
      map.getLayers().changed();
    }

    this._updateMapSelectedLayer(layers);

    // Some lifecycle actions depend on selection, thus this needs to be called afterwards
    // e.g. see feature-acquisition
    newLayers.forEach(layer => executeLayerLifecycleActions(layer, map, 'Open'));

    this.#changedLayerIds = undefined; // Reset after updateLayers().
  }

  _updateMapSelectedLayer (layers: ClassicLayerDto[]) {
    const map = this.#map;
    const selectedLayerInModel = layers.find(layer => layer[LAYER_SELECTED_PROPERTY]);
    if (selectedLayerInModel && selectedLayerInModel.visibleInLayerList) {
      map.set(MapProperty.SELECTED_LAYER, this.getLayer(selectedLayerInModel.id));
    } else {
      if (!map.get(MapProperty.SELECTED_LAYER)) {
        map.set(MapProperty.SELECTED_LAYER, this.layers.slice().reverse().find(layer => layer.get(LayerProperty.VISIBLE_IN_LAYERLIST)));
      }
      if (selectedLayerInModel || map.get(MapProperty.SELECTED_LAYER)) {
        this._pushIfNotPulling(); // override server state
      }
    }
  }

  pushUnthrottled () {
    return this._pushIfNotPulling();
  }

  _pushIfNotPulling () {
    return this.#isPullInProgress ? Promise.resolve() : this._push();
  }

  push () {
    return this.#pushIfNotPullingThrottled()!;
  }

  _push () {
    if (this.#contextPushEnabled) {
      const map = this.#map;
      const selectedLayer = this.#map.get(MapProperty.SELECTED_LAYER);

      const normalGroupChoices: NormalGroupChoices = {};
      this.layers.reduce((byLayer, layer) => {
        byLayer[layerId(layer)] = layer.get(LayerProperty.NORMAL_GROUP_CHOICES) as GroupChoiceDto;
        return byLayer;
      },
      normalGroupChoices);

      const pushPromise = this.#mapContextApi.updateContext(
        {
          size: map.getInnerSize(),
          // It is important to subtract padding from the map size and the extent.
          // Otherwise, the map view will be shifted by the size of the toolbar on each page reload,
          // as padding is taken into account when fitting the map view.
          extent: map.getView().calculateVisibleExtent(),
          scale: this.mapScale,
          layers: this.layers.map(layerIdForPush),
          hiddenLayers: this.layers.filter((layer) => !layer.getVisible()).map(layerId),
          selectedLayer: selectedLayer && layerId(selectedLayer),
          normalGroupChoices
        }
      );
      pushPromise.finally(() => {
        this.dispatchEvent('mapStatePushed');
      });
      return pushPromise;
    } else {
      return Promise.resolve();
    }
  }

  getLayer (id: string) {
    return getAllLayers(this.layers).find((layer) => layerId(layer) === id);
  }

  get layers () {
    return [ ...this.#map.getLayers().getArray() ] as BaseLayer[];
  }

  get sourceLayers () {
    return [ ...this.#map.getAllLayers() ];
  }

  get visibleLayers () {
    return this.#map.getAllLayers().filter(layer => layer.getVisible());
  }

  _setLayers (layers: BaseLayer[]) {
    // this mutates the layer-array.
    const oldLayers = this.#map.getLayers().getArray();
    oldLayers.splice(0, oldLayers.length, ...layers);
  }

  getCanceledLayers () {
    return this.layers.filter(layer => layer.get(LayerProperty.TASKS)?.hasRestartableTasks());
  }

  getOnDemandLayers () {
    return this.layers
      .filter(layer => layer instanceof Layer && layer.getSource()!.get(SourceProperty.WORKBOOK_VIEW_ON_DEMAND) === WORKBOOK_VIEW_ON_DEMAND.ON);
  }

  isVisible (layer: BaseLayer) {
    return layer.get(LayerProperty.VISIBLE);
  }

  set loading (loading) {
    if (loading) {
      this.#mapProgressSpinner.show();
    } else {
      this.#mapProgressSpinner.hide();
    }
  }

  get loading () {
    return this.#mapProgressSpinner.visible;
  }

  cancelLoading () {
    this.layers.forEach(layer => {
      const layerTasks = layer.get(LayerProperty.TASKS);
      if (layerTasks && layerTasks.hasOngoingTasks()) {
        layerTasks.cancelTasks();
      }
    });
  }

  highlightFeatures (features: Feature[]) {
    this.#highlightHandler.highlightFeatures(features);
  }

  resetHighlighting () {
    this.#highlightHandler.resetHighlighting();
  }

  zoomToLayer (layerId: string) {
    this.zoomToLayers([ layerId ]);
  }

  zoomToLayers (layerIds: string[]) {
    const extent = layerIds
      .map(layerId => this.getLayer(layerId))
      .map(layer => layer && layer.get(LayerProperty.SUGGESTED_EXTENT))
      .filter(Boolean) // filter out undefined layer extents
      .reduce((extent, layerExtent) => extend(extent, layerExtent), createEmpty());

    if (!isEmpty(extent)) {
      this.#map.getView().fit(extent);
    }
  }

  get mapScale () {
    const view = this.#map.getView();
    if (!view.getResolution()) {
      view.setResolution(view.getResolutionForZoom(view.getZoom()!));
    }
    return scaleForResolution(view, view.getResolution()!);
  }

  set mapScale (scale) {
    const view = this.#map.getView();
    view.setResolution(resolutionForScale(view, scale));
  }

  getCurrentPrimaryInteractionIfResettable () {
    const primaryInteraction = this.#map.get(MapProperty.PRIMARY_INTERACTION);
    return primaryInteraction?.resettable ? primaryInteraction : null;
  }

  getProjection () {
    return this.#map.getView().getProjection();
  }

  setChangedLayerIds (changedLayerIds: string[]) {
    this.#changedLayerIds = changedLayerIds;
  }

}

function layerIdForPush (layer: BaseLayer) {
  const id = layer.get(LayerProperty.ID);
  const activeLayerIndex = layer.get(LayerProperty.TOGGLE_GROUP_ACTIVE_LAYER_INDEX);
  return activeLayerIndex != null ? `${id}:${activeLayerIndex}` : id;
}

function layerId (layer: BaseLayer) {
  return layer.get(LayerProperty.ID) as string;
}

function executeLayerLifecycleActions (layer: BaseLayer, map: Map, lifecyclePhase: string) {
  const lifecycleActions = layer.get(LayerProperty.LIFECYCLE_ACTIONS) as Record<string, string[]>;
  if (lifecycleActions) {
    const layerOpenLifecycleActions = lifecycleActions[lifecyclePhase];
    if (layerOpenLifecycleActions && layerOpenLifecycleActions.length) {
      const allLayerActionsMapping = (map.get(MapProperty.LAYER_ACTIONS_PROVIDER) as (layer: BaseLayer) => Record<string, LayerAction>)(layer);
      layerOpenLifecycleActions.forEach(lifecycleAction => {
        const layerLifecycleAction = allLayerActionsMapping[lifecycleAction];
        if (layerLifecycleAction) {
          layerLifecycleAction.isSupported(getActionContext(map)).then(supported => {
            if (supported) {
              layerLifecycleAction.execute();
            }
          }).catch((err: Error) => logger.error('Could not check if lifecycleAction is supported ', err));
        }
      });
    }
  }
}

function calculateMinMaxResolutions (
  projection: Projection,
  maxExtent: Extent,
  mapSize: DimensionDto,
  minAllowedScale: number,
  maxAllowedScale: number) {
  // Dummy view is needed to calculate maxResolution for the real map view. There is no other way to supply maxResolution after View object construction.
  const dummyView = new View({ projection });
  let maxResolution = dummyView.getResolutionForExtent(maxExtent, mapSize);
  /*
      Based on how OL2 calculates min resolution - relevant OL2 code can be found here:
      https://github.com/openlayers/ol2/blob/master/lib/OpenLayers/Layer.js#L838
      Current calculation is based on expectation that the maxExtent option was the only relevant attribute (option)
      initially passed to OL2 in cadenza application.
  */
  let minResolution = maxResolution / Math.pow(ZOOM_SCALE_FACTOR, DEFAULT_NUMBER_OF_ZOOM_LEVELS - 1);

  // For Web Mercator projections maps, maxResolution is adjusted to match standard web mercator zoom levels and avoid map image scaling.
  // minResolution is always the same - the 25th web mercator zoom level
  if (isWebMercatorProjection(projection)) {
    const maxWebMercatorResolution = getWebMercatorMaxResolution();
    let adjustedMaxResolution = maxWebMercatorResolution;
    while (adjustedMaxResolution > maxResolution) {
      adjustedMaxResolution /= ZOOM_SCALE_FACTOR;
    }
    maxResolution = roundToFloatOrInt(adjustedMaxResolution, 3);
    minResolution = roundToFloatOrInt(maxWebMercatorResolution / Math.pow(ZOOM_SCALE_FACTOR, DEFAULT_NUMBER_OF_ZOOM_LEVELS - 1), 3);
  }
  // overwrite any calculated values if the min/maxAllowedScale is set in the mml file
  if (isMinAllowedScaleSet(minAllowedScale)) {
    minResolution = scaleToResolution(minAllowedScale);
  }
  if (isMaxAllowedScaleSet(maxAllowedScale)) {
    maxResolution = scaleToResolution(maxAllowedScale);
  }

  return { minResolution, maxResolution };
}

function isWebMercatorProjection (projection: Projection) {
  return WEB_MERCATOR_PROJECTIONS.some(p => p.getCode() === projection.getCode());
}

function getWebMercatorMaxResolution () {
  return maxResolutionFromExtent(WEB_MERCATOR_PROJECTIONS[0].getExtent());
}

/**
 * Creates max resolution from an extent. Inspired by OL function ol/tilegrid~resolutionsFromExtent.
 *
 * @param extent - Extent
 * @param [tileSize] - Tile size
 * @param [tileSize.tileWidth]
 * @param [tileSize.tileHeight]
 * @return maximal resolution
 */
function maxResolutionFromExtent (extent: Extent, { tileWidth = 256, tileHeight = 256 } = {}) {
  return Math.max(getWidth(extent) / tileWidth, getHeight(extent) / tileHeight);
}

function isMinAllowedScaleSet (minAllowedScale: number) {
  return minAllowedScale !== null && minAllowedScale !== undefined && minAllowedScale !== 0;
}

function isMaxAllowedScaleSet (maxAllowedScale: number) {
  return maxAllowedScale !== null && maxAllowedScale !== undefined && maxAllowedScale !== 0 && maxAllowedScale < Number.MAX_VALUE;
}
